import { select, take, takeLatest, call } from '@redux-saga/core/effects';
import { LOCATION_CHANGE } from 'connected-react-router';
import {
  FS_CHECKOUT_LOADED,
  FS_CHECKOUT_ABORTED,
  FS_CHECKOUT_SUCCESS,
  FS_CHECKOUT_ERROR,
  FS_CLOSE_POPOVER_MODAL,
  FS_LINK_OUT
} from 'JS/actionCreators/checkoutActions';
import { getScribeById } from 'JS/sagas/selectors';
import countries from 'JS/config/countries.yaml';
import gaTracking from 'JS/shared/helpers/tracking';
import { ROUTE_CODES } from 'JS/config/routes';

import { track, setUserProperties, registerEventSuperProperties } from '../mixpanelProvider';

function* trackFastSpringCheckout() {
  const { trigger = 'Direct', extraEventProps } = yield select(state => state.checkout.upgradeTrackingProperties || {});
  const triggerPath = yield select(({ router }) => router.location?.pathname);
  const match = triggerPath.match(/\/editor\/(.+)/);
  const scribeProps = {};

  if (match?.[1]) {
    const scribe = yield select(state => getScribeById(state, match[1]));
    if (scribe) {
      scribeProps['Scribe ID'] = scribe.id;
      scribeProps['Scribe Type'] = scribe.source?.type;
    }
  }

  const checkoutProps = {
    ...extraEventProps,
    'Upgrade Trigger': trigger,
    'Checkout Provider': 'FastSpring',
    'Existing Payment Details': false
  };

  yield call(track, 'Upgrade Sub Choice Modal Displayed', {
    'Upgrade Trigger': trigger,
    ...scribeProps,
    ...checkoutProps
  });

  while (true) {
    const nextCheckoutAction = yield take([
      FS_CHECKOUT_ABORTED,
      FS_CHECKOUT_SUCCESS,
      FS_CHECKOUT_ERROR,
      FS_CHECKOUT_LOADED,
      FS_CLOSE_POPOVER_MODAL,
      FS_LINK_OUT
    ]);

    const selectedProduct = yield select(state => state.checkout.selectedProduct);
    const selectedProductProps = {
      'Selected Product ID': selectedProduct
    };

    switch (nextCheckoutAction.type) {
      case FS_LINK_OUT: {
        yield call(track, 'Compare Plans', { ...scribeProps, ...checkoutProps, ...selectedProductProps });
        break;
      }

      case FS_CHECKOUT_LOADED: {
        yield call(track, 'Upgrade Modal Displayed', { ...scribeProps, ...checkoutProps, ...selectedProductProps });
        break;
      }

      case FS_CLOSE_POPOVER_MODAL: {
        yield call(track, 'Abort Payment', {
          ...scribeProps,
          ...checkoutProps,
          ...selectedProductProps
        });
        break;
      }

      case FS_CHECKOUT_ABORTED: {
        yield call(track, 'Abort Upgrade', {
          ...scribeProps,
          ...checkoutProps,
          ...selectedProductProps
        });
        return yield call(
          { fn: gaTracking.event, context: gaTracking },
          {
            category: 'Upgrade',
            action: 'CLOSE_MODAL'
          }
        );
      }

      case FS_CHECKOUT_ERROR: {
        yield call(track, 'Purchase Now', {
          ...scribeProps,
          ...checkoutProps,
          ...selectedProductProps,
          Result: 'Failure',
          'Failure Reason': nextCheckoutAction.error.message
        });
        yield call(
          { fn: gaTracking.event, context: gaTracking },
          {
            category: 'Upgrade',
            action: 'PAYMENT_FAILED',
            label: nextCheckoutAction.error.message
          }
        );
        break;
      }

      case FS_CHECKOUT_SUCCESS: {
        const countryCode = countries.find(country => country.isoCode === nextCheckoutAction.orderDetails.country)
          ?.isoCode3;
        const sku = nextCheckoutAction.orderDetails.items[0]?.sku;

        yield call(track, 'Purchase Now', {
          ...scribeProps,
          ...checkoutProps,
          ...selectedProductProps,
          Result: 'Success',
          'Payment Country': countryCode,
          Currency: nextCheckoutAction.orderDetails.currency,
          'Product SKU': sku
        });

        const [, skuTier, term] = sku.split('_');
        const tier = `TIER${skuTier}`;

        const userProperties = {
          'Subscription Tier': tier,
          'Licence Type': tier,
          'Subscription Term': term
        };

        yield call(setUserProperties, userProperties);
        yield call(registerEventSuperProperties, userProperties);

        return call(
          { fn: gaTracking.event, context: gaTracking },
          {
            category: 'Upgrade',
            action: 'PAYMENT_SUCCESS',
            label: countryCode
          }
        );
      }

      default:
        break;
    }
  }
}

function* trackFastSpringCheckoutListener({ payload }) {
  const { pathname } = payload.location;

  if (pathname.includes(ROUTE_CODES.UPGRADE) && !pathname.includes(ROUTE_CODES.UPGRADE_LEGACY)) {
    yield call(trackFastSpringCheckout);
  }
}

export default function* trackFastSpring() {
  yield takeLatest([LOCATION_CHANGE], trackFastSpringCheckoutListener);
}
